import type { ExecuteWorkflowNodeRequest } from '@unifyapps/network/generated/models/executeWorkflowNodeRequest';
import { keepPreviousData, useInfiniteQuery } from '@unifyapps/network/react-query';
import type { JSONSchema7 } from 'json-schema';
import merge from 'lodash/merge';
import _omit from 'lodash/omit';
import { useMemo } from 'react';
import { useOfflineDataSourceHandler } from '../../../../../offline-mode/hooks/useOfflineDataSourceHandler';
import type { QueryRequestResult } from '../../../../stores/GlobalStateStore';
import { useAdaptInfiniteDataSourceResponse } from '../../hooks/useAdaptDataSourceResponse';
import type { DataSourceQueryOptions } from '../../types';
import type { DebugParams, ExecuteWorkflowNodeQueryKeyType } from '../DataSourceWorkflowNode/api';
import {
  executeRunGetSchemaWorkflowNode,
  executeWorkflowNode,
  useExecuteWorkflowNodeQueryKeyMemo,
} from '../DataSourceWorkflowNode/api';
import type { InfinitePaginationInfo } from './types';
import {
  getHasNextPage,
  getInfiniteDataSourceWorkflowNodePageParams,
  getInitialPageParams,
  getNextPageParams,
} from './utils/pageParams';

interface ExecuteWorkflowNodeResponseType {
  response: unknown;
}

type UseInfiniteDataSourceWorkflowNodeProps = {
  payload: ExecuteWorkflowNodeRequest;
  debugParams: DebugParams;
  queryOptions?: Partial<DataSourceQueryOptions>;
  enabled?: boolean;
  infinitePaginationInfo: InfinitePaginationInfo;
  options?: {
    isTransformerConfigured?: boolean;
    isBuilderMode?: boolean;
    pathToOmitFromQueryKey?: string[];
  };
};

type InfiniteDataSourceWorkflowNodeQueryRequestResult = QueryRequestResult & {
  outputSchema?: JSONSchema7;
};

function useInfiniteDataSourceWorkflowNode({
  payload: initialPayload,
  debugParams,
  queryOptions,
  enabled,
  infinitePaginationInfo,
  options,
}: UseInfiniteDataSourceWorkflowNodeProps): InfiniteDataSourceWorkflowNodeQueryRequestResult {
  const { offset, limit, payload, paginateBy } = useMemo(
    () => getInfiniteDataSourceWorkflowNodePageParams(initialPayload, infinitePaginationInfo),
    [initialPayload, infinitePaginationInfo],
  );

  const { shouldUseOfflineMode, offlineQueryFunction } = useOfflineDataSourceHandler({
    payload: initialPayload,
  });

  const isTransformerConfigured = Boolean(options?.isTransformerConfigured);
  const isRunAndGetSchema = Boolean(options?.isBuilderMode && isTransformerConfigured);

  const queryKeyType = useMemo<ExecuteWorkflowNodeQueryKeyType>(() => {
    if (isRunAndGetSchema) return 'api-rungetschema';

    /**
     * Earlier, we were using the `api` query key type, and it was the same in useDataSourceWorkflowNode.
     * Now when some data source was being mapped to a block with always on infinite pagination by default(for example timeline),
     * and some data source was being mapped in such a block, it used to return the same data initially.
     * This data would not have pages and react query internally does pages.length in getNextPageParam
     * react-query's query-core/src/infiniteQueryBehavior.ts where pages would be undefined and hence the function would throw an error.
     */
    return 'infinite-paginated-api';
  }, [isRunAndGetSchema]);

  const modifiedQueryKey = useExecuteWorkflowNodeQueryKeyMemo(
    _omit(payload, options?.pathToOmitFromQueryKey ?? []),
    debugParams,
    queryKeyType,
  );

  const {
    isLoading,
    isFetching,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    error: fetchError,
    data: initialData,
    refetch,
  } = useInfiniteQuery({
    queryKey: modifiedQueryKey,
    queryFn: ({ pageParam, meta }) => {
      const mergedPayload = merge({}, payload, pageParam);
      const debugParamsWithMeta = { ...debugParams, meta };

      // if in offline mode, handle it by WatermelonDB handler
      if (shouldUseOfflineMode) {
        return offlineQueryFunction(mergedPayload);
      }

      return isRunAndGetSchema
        ? executeRunGetSchemaWorkflowNode(mergedPayload, debugParamsWithMeta)
        : executeWorkflowNode(mergedPayload, debugParamsWithMeta);
    },
    getNextPageParam: (lastPage: ExecuteWorkflowNodeResponseType | undefined, _, lastPageParam) => {
      if (
        getHasNextPage({ lastPage, lastPageParam, infinitePaginationInfo, isTransformerConfigured })
      ) {
        return getNextPageParams(lastPageParam, infinitePaginationInfo, paginateBy);
      }
      return null;
    },
    initialPageParam: getInitialPageParams({ offset, limit, infinitePaginationInfo, paginateBy }),
    placeholderData: keepPreviousData,
    enabled,
    ...queryOptions,
  });

  const { data, outputSchema } = useAdaptInfiniteDataSourceResponse({
    initialData,
    payload,
    isTransformerConfigured,
    infinitePaginationInfo,
  });

  return {
    error: fetchError,
    isLoading,
    isFetching,
    data,
    queryKey: modifiedQueryKey,
    inputs: payload.inputs,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    refetch,
    outputSchema,
  };
}

export default useInfiniteDataSourceWorkflowNode;
